import { AttachedFile, Button } from '@/components';
import { Flex, Body2Light, Body3Light, TrashIcon, Container } from '@/ui';
import styled from 'styled-components';
import { COLORS } from '@/utils/static';
import { Modal, Popconfirm, Input, Button as AntdButton } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConstructorApi } from '@/features/constructorProducts/api';
import { usePopups } from '@/hooks';
import { useProduct } from '@/features/constructorProducts/hooks';
import { useParams } from 'react-router';

export const DocumentLine = ({
  doc,
  validTill,
  paddingRight,
  onDelete = null,
  withRename = false,
  index,
  hideLastBorder,
  hideBorder
}) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);

  return (
    <MainRow
      align={'center'}
      justify='space-between'
      $margin={-10}
      $hideLastBorder={hideLastBorder}
      $hideBorder={hideBorder}
      gap={6}
    >
      <Flex align='center' gap={3}>
        {index && (
          <Body2Light $color={COLORS.blueAntd} style={{ flexShrink: 0 }}>
            {index}.
          </Body2Light>
        )}
        <DocRow justify={'space-between'} $paddingRight={paddingRight} wrap gap={12}>
          <Flex align={'center'} wrap>
            <AttachedFile isLong title={doc.name} url={doc.documentURL} />
            <Body3Light $marginLeft={16}>{validTill}</Body3Light>
          </Flex>
        </DocRow>
      </Flex>

      <Flex gap={10}>
        {withRename && (
          <Button
            text={t('Rename')}
            size='small'
            block={false}
            onClick={() => setOpenModal(true)}
          />
        )}
        {onDelete && <TrashIcon onClick={() => onDelete(doc.id)} />}
      </Flex>

      <ModalRenameDoc
        defaultValue={doc.name}
        docId={doc.id}
        open={openModal}
        closeModal={() => setOpenModal(false)}
      />
    </MainRow>
  );
};

const ModalRenameDoc = ({ docId, defaultValue, open, closeModal }) => {
  const { t } = useTranslation();
  const { id: productId } = useParams();
  const { showSuccessToast, showErrorToast, showInfoToast } = usePopups();
  const { refetch } = useProduct(productId);
  const [value, setValue] = useState(defaultValue ?? '');

  const makeRequest = async () => {
    if (value && value !== defaultValue) {
      try {
        await ConstructorApi.renameFileToProduct({
          productFileToDownloadId: docId,
          name: value.trim()
        });

        await refetch();
        showSuccessToast('Document name is changed');
        setValue('');
        closeModal();
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    } else {
      showInfoToast('Please enter new name');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      title={t('Rename document')}
      okText={t('Rename')}
      footer={[
        <AntdButton key='back' onClick={closeModal}>
          {t('Cancel')}
        </AntdButton>,
        <Popconfirm
          title={t('Rename document')}
          description={t('Are you sure you want to rename this document?')}
          okText={t('Yes')}
          cancelText={t('No')}
          onConfirm={makeRequest}
        >
          <AntdButton type='primary'>{t('Rename')}</AntdButton>
        </Popconfirm>
      ]}
    >
      <Container $marginTop={10} $marginBottom={15}>
        <Input value={value} onChange={(e) => setValue(e.target.value)} />
      </Container>
    </Modal>
  );
};

const DocRow = styled(Flex)`
  min-height: 56px;
  padding: 12px 0 12px 0;
  ${(p) => (p.$paddingRight ? `padding-right: ${p.$paddingRight}px;` : '')}
`;

const MainRow = styled(Flex)`
  border-bottom: ${(p) => (p.$hideBorder ? 'none' : ` 1px solid ${COLORS.greyBasic}`)};

  ${(p) =>
    p.$hideLastBorder
      ? `
    &:last-child {
      border-bottom: none;
    }
  `
      : ''}
`;
